import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient, useInvalidateProposalInformation } from 'bff';
import {
  Result,
  ResultStates,
  ThirdPartiesDeleteAllRequest,
  ThirdPartiesDeleteRequest,
  ThirdPartiesGetRequest,
  ThirdPartiesGetThirdPartyRequest,
  ThirdPartiesPostRequest
} from 'bff/client';
import { UseDisplayErrorMutationOptions } from 'bff/types/display-error-props.type';
import { secondsToMilliseconds } from 'date-fns';
import { clientService } from 'stores/client-store/client-store';
import { useInvalidateJustPortfolioDetails } from '../just-portfolio/JustPortfolioApi';
import { useInvalidatePaymentDetails } from '../payment-details/PaymentDetailsApi';

export const thirdPartiesKeys = {
  base: () => ['third-parties'] as const,
  fetch: (request: ThirdPartiesGetRequest) => [...thirdPartiesKeys.base(), request] as const,
  fetchThirdParty: (request: ThirdPartiesGetThirdPartyRequest) =>
    ['fetch-third-party', request] as const,
  submit: () => [...thirdPartiesKeys.base(), 'submit'] as const,
  delete: () => [...thirdPartiesKeys.base(), 'delete'] as const,
  deleteAll: () => [...thirdPartiesKeys.base(), 'delete-all'] as const
};

export const useInvalidateThirdParties = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: thirdPartiesKeys.base(),
      refetchType: 'all'
    });
};

export const getThirdParties = (getThirdPartiesRequest: ThirdPartiesGetRequest) => {
  const { thirdPartiesApi } = clientService.getApiClient();

  return queryOptions({
    staleTime: secondsToMilliseconds(30),
    queryKey: thirdPartiesKeys.fetch(getThirdPartiesRequest),
    queryFn: () => thirdPartiesApi.thirdPartiesGet(getThirdPartiesRequest),
    select: (response) => response.data!
  });
};

export const getThirdParty = (getThirdPartyRequest: ThirdPartiesGetThirdPartyRequest) => {
  const { thirdPartiesApi } = clientService.getApiClient();

  return queryOptions({
    staleTime: secondsToMilliseconds(30),
    queryKey: thirdPartiesKeys.fetchThirdParty(getThirdPartyRequest),
    queryFn: () => thirdPartiesApi.thirdPartiesGetThirdParty(getThirdPartyRequest),
    select: (response) => response.data!
  });
};

export const useDeleteThirdPartyRelation = (
  options?: UseDisplayErrorMutationOptions<Result, ThirdPartiesDeleteRequest>
) => {
  const { thirdPartiesApi } = useApiClient();
  const queryClient = useQueryClient();

  const invalidatePaymentDetails = useInvalidatePaymentDetails();
  const invalidateJustPortfolioDetails = useInvalidateJustPortfolioDetails();

  return useMutation({
    mutationKey: thirdPartiesKeys.delete(),
    mutationFn: (request: ThirdPartiesDeleteRequest) => thirdPartiesApi.thirdPartiesDelete(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        await Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: thirdPartiesKeys.base(),
            refetchType: 'all'
          }),
          invalidatePaymentDetails(),
          invalidateJustPortfolioDetails()
        ]);
        options?.onSuccess?.(data, variables, ctx);
      }
    }
  });
};

export const useDeleteAllThirdPartyRelations = (
  options?: UseDisplayErrorMutationOptions<Result, ThirdPartiesDeleteAllRequest>
) => {
  const { thirdPartiesApi } = useApiClient();
  const queryClient = useQueryClient();

  const invalidatePaymentDetails = useInvalidatePaymentDetails();

  return useMutation({
    mutationKey: thirdPartiesKeys.deleteAll(),
    mutationFn: (request: ThirdPartiesDeleteAllRequest) =>
      thirdPartiesApi.thirdPartiesDeleteAll(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        await Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: thirdPartiesKeys.base(),
            refetchType: 'all'
          }),
          invalidatePaymentDetails()
        ]);
        options?.onSuccess?.(data, variables, ctx);
      }
    }
  });
};

export const useSaveIndividualThirdParties = (
  options?: UseDisplayErrorMutationOptions<Result, ThirdPartiesPostRequest>
) => {
  const { thirdPartiesApi } = useApiClient();

  const invalidatePaymentDetails = useInvalidatePaymentDetails();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: thirdPartiesKeys.submit(),
    mutationFn: (request: ThirdPartiesPostRequest) => thirdPartiesApi.thirdPartiesPost(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        await Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: thirdPartiesKeys.base(),
            refetchType: 'all'
          }),
          invalidatePaymentDetails(),
          invalidateProposalInformation()
        ]);
        options?.onSuccess?.(data, variables, ctx);
      }
    }
  });
};
